import { AppButton, Divider } from '@clickguard/design-system'
import LayersTwo01 from '@untitled-ui/icons-react/build/cjs/LayersTwo01'
import Trash01 from '@untitled-ui/icons-react/build/cjs/Trash01'
import { Copy01 } from 'components/icons'
import { useState } from 'react'
import { useRuleActivity } from './rule-activity'
import { RuleLedger } from './rule-ledger'
import { STAGGER_MS } from './rules-activity-chart'
import { SwapValue, swapDelay } from './swap-value'
import { CustomRule } from './types'

const sum = (series: number[]): number =>
	series.reduce((total, value) => total + value, 0)

/** A rule's activity over the picked time frame, one bar per day against the
 *  period's busiest. Hovering swaps the caption rather than opening 14 tooltips in
 *  a panel mounted for every row. */
const ActivityChart = ({
	executions,
	days
}: {
	executions: number[]
	days: string[]
}) => {
	const [hovered, setHovered] = useState<number | null>(null)
	const peak = Math.max(1, ...executions)
	const day = hovered === null ? null : hovered
	// Sweep left to right over the bars on screen, so a 7-day rail and a 30-day one
	// take the same time — the same arithmetic the big chart does.
	const step = executions.length > 1 ? STAGGER_MS / (executions.length - 1) : 0

	return (
		<div className="flex flex-col gap-1.5">
			<div
				className="flex h-12 items-end gap-[3px]"
				role="img"
				aria-label={`Daily executions for the last ${executions.length} days`}
				onMouseLeave={() => setHovered(null)}
			>
				{executions.map((value, index) => (
					<div
						key={index}
						className="flex h-full flex-1 cursor-default flex-col justify-end"
						onMouseEnter={() => setHovered(index)}
					>
						<div
							className="custom-rules-rail-bar w-full rounded-[2px]"
							style={{
								height: `${Math.max(4, (value / peak) * 100)}%`,
								backgroundColor: 'var(--cg-chart-violet)',
								opacity: day === null || day === index ? 1 : 0.45,
								animationDelay: `${index * step}ms`
							}}
						/>
					</div>
				))}
			</div>
			{/* Both captions: the whole chart is remounted on a window change, so a
			    caption left out would be the only thing that did not move. */}
			{/* The two ends of the axis take the two ends of the sweep, so the dates
			    land with the bars above them. */}
			<div className="flex items-baseline justify-between text-[11px] text-[rgb(var(--cg-text-secondary))]">
				<SwapValue token={days[0]} delay={swapDelay(0, 2)}>
					{days[0]}
				</SwapValue>
				<SwapValue token={days[days.length - 1]} delay={swapDelay(1, 2)}>
					{days[days.length - 1]}
				</SwapValue>
			</div>
			{/* Idle caption is a blank line, and still occupies its box so the panel
			    does not jump on hover. */}
			<span className="text-xs text-[rgb(var(--cg-text-secondary))]">
				{day === null
					? '\u00a0'
					: `${days[day]} · ${executions[day].toLocaleString()} executions`}
			</span>
		</div>
	)
}

interface RuleDetailsDrawerProps {
	rule: CustomRule
	/** Header time frame — the rail charts the series' last N days. */
	days: number
	onDuplicate: () => void
	onDelete: () => void
	onSaveAsTemplate: () => void
}

/** Inline breakdown for a rule row — the PA dimension-grid pattern, on the builder's
 *  own WHERE / WHEN / AND / THEN spine. Duplicate / Delete live here now that the
 *  row kebab is gone. */
export const RuleDetailsDrawer = ({
	rule,
	days,
	onDuplicate,
	onDelete,
	onSaveAsTemplate
}: RuleDetailsDrawerProps) => {
	const activityWindow = useRuleActivity()
	const activity = activityWindow.activity.find((a) => a.ruleId === rule.id)
	const executions = (activity?.executions ?? []).slice(-days)
	// Measured over the same window the chart draws, so the number and the
	// picture can't disagree; the row's lifetime totals are a different thing.
	const windowTotal = sum(executions)
	// The window right before this one. The fetch spans twice the time frame
	// precisely so both halves come out of one series (see rule-activity.tsx).
	const previousTotal = sum(
		(activity?.executions ?? []).slice(-2 * days, -days)
	)
	const delta = Math.round(
		((windowTotal - previousTotal) / Math.max(1, previousTotal)) * 100
	)
	return (
		<div className="bg-[rgb(var(--cg-canvas))] px-6 py-5">
			<div className="grid gap-x-10 gap-y-6 lg:grid-cols-[minmax(0,1fr)_240px]">
				<RuleLedger rule={rule} />

				{/* Is it still catching things? Not answerable from the row's
				    lifetime totals. */}
				<div className="flex flex-col gap-3 self-start lg:border-l lg:border-[rgb(var(--cg-border))] lg:pl-8">
					{/* The heading answers the switch too: if it changed silently while the
					    numbers moved, the rail would read as new data for the same period. */}
					<span className="text-[11px] font-semibold uppercase leading-4 tracking-wide text-[rgb(var(--cg-text-secondary))]">
						<SwapValue token={days}>Last {days} days</SwapValue>
					</span>
					<div className="flex items-baseline gap-1.5">
						<span className="text-2xl font-semibold leading-none tabular-nums text-[rgb(var(--cg-text-primary))]">
							<SwapValue token={windowTotal}>
								{windowTotal.toLocaleString()}
							</SwapValue>
						</span>
						<span className="text-xs text-[rgb(var(--cg-text-secondary))]">
							executions
						</span>
					</div>
					{/* A rule that did not fire gets the period and the count alone — no chart,
					    no comparison (§3.1). Gated on the TOTAL, not the array's length: the
					    series is densified, so a rule that never fired still arrives as zeros. */}
					{windowTotal > 0 && (
						<>
							<span className="text-xs text-[rgb(var(--cg-text-secondary))]">
								<SwapValue token={`${delta}:${days}`}>
									{delta >= 0 ? '+' : ''}
									{delta}% vs previous {days} days
								</SwapValue>
							</span>
							{/* Keyed so the bars rise again on a window change: without it React keeps
							    the first seven bar divs and only rewrites their heights. The remount
							    also drops `hovered`. */}
							<ActivityChart
								key={days}
								executions={executions}
								days={activityWindow.days.slice(-days)}
							/>
						</>
					)}
				</div>
			</div>

			<Divider className="my-4" />

			<div className="flex items-center justify-end gap-3">
				<div className="flex items-center gap-2">
					<AppButton
						variant="secondary"
						size="sm"
						label="Save as template"
						leftIcon={<LayersTwo01 />}
						onClick={onSaveAsTemplate}
					/>
					<AppButton
						variant="secondary"
						size="sm"
						label="Duplicate"
						leftIcon={<Copy01 />}
						onClick={onDuplicate}
					/>
					<AppButton
						variant="secondary"
						size="sm"
						label="Delete"
						leftIcon={<Trash01 />}
						onClick={onDelete}
						className="!text-[rgb(var(--cg-danger))] [&_svg]:!text-[rgb(var(--cg-danger))]"
					/>
				</div>
			</div>
		</div>
	)
}
